import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { AdminOutputs } from 'shared/api'
import { useAdminTrpc } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'

type Purchase = AdminOutputs['purchases']['ListHistory']['items'][number]

/** Only lines with money still refundable are offered. */
function remaining(line: Purchase['lines'][number]): number {
	return line.quantity - line.refundedQuantity
}

export function RefundButton({ purchase }: { purchase: Purchase }) {
	const trpc = useAdminTrpc()
	const { t, prefixT } = useTranslate('pages.adminPurchases')
	const refundableLines = useMemo(
		() => purchase.lines.filter((line) => remaining(line) > 0),
		[purchase.lines],
	)
	const [open, setOpen] = useState(false)
	const [quantities, setQuantities] = useState<Record<string, string>>({})
	// Idempotency-key policy (mirrors WalletSection.tsx): rotate only after a
	// committed refund, so a re-click after a lost response replays the same
	// partial refund instead of refunding twice.
	const [idemKey, setIdemKey] = useState(() => crypto.randomUUID())
	const refund = useMutation(trpc.purchases.Refund.mutationOptions())

	function setQuantity(inventoryId: string, value: string, max: number) {
		const digits = Number(value.replace(/\D/g, ''))
		const clamped = Number.isNaN(digits) ? 0 : Math.min(digits, max)
		setQuantities((m) => ({ ...m, [inventoryId]: clamped ? String(clamped) : '' }))
	}

	const selected = refundableLines
		.map((line) => ({ inventoryId: line.inventoryId, quantity: Number(quantities[line.inventoryId] ?? '') || 0 }))
		.filter((line) => line.quantity > 0)

	async function handleRefund() {
		if (selected.length === 0) {
			toast.error(prefixT('refundNothingSelected'))
			return
		}
		try {
			const res = await refund.mutateAsync({
				purchaseId: purchase._id,
				lines: selected,
				idempotencyKey: idemKey,
			})
			toast.success(
				prefixT('refundSuccess', {
					amount: t('common.amountUzs', { amount: formatUzsAmount(res.refundedUzs) }),
					balance: t('common.amountUzs', { amount: formatUzsAmount(res.balanceAfterUzs) }),
				}),
			)
			setIdemKey(crypto.randomUUID())
			setQuantities({})
			setOpen(false)
		} catch {
			toast.error(prefixT('refundFailed'))
		}
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger
				render={
					<Button size='sm' variant='outline'>
						{prefixT('refund')}
					</Button>
				}
			/>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{prefixT('refundTitle')}</DialogTitle>
				</DialogHeader>
				<div className='flex flex-col gap-2'>
					{refundableLines.map((line) => {
						const max = remaining(line)
						return (
							<div key={line.inventoryId} className='flex items-center gap-2'>
								<span className='flex-1 text-sm'>
									{prefixT('lineLabel', { name: line.name, quantity: String(line.quantity) })}
									<span className='ml-1 text-muted-foreground'>
										{prefixT('refundableRemaining', { count: String(max) })}
									</span>
								</span>
								<Input
									className='w-20'
									inputMode='numeric'
									placeholder='0'
									value={quantities[line.inventoryId] ?? ''}
									onChange={(e) => setQuantity(line.inventoryId, e.target.value, max)}
								/>
							</div>
						)
					})}
				</div>
				<DialogFooter>
					<Button
						variant='destructive'
						disabled={refund.isPending || selected.length === 0}
						onClick={handleRefund}
					>
						{prefixT('refundSubmit')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
